/** Decoded JWT payload fields relevant to LOFT auth. */
export interface TokenPayload {
  sub: string;
  email?: string;
  exp?: number;
  iat?: number;
  'cognito:username'?: string;
  [key: string]: unknown;
}

// Cognito auth configuration — client IDs are public OAuth2 values (not secrets).
// Injected at build time from the LOFT CloudFormation stack outputs:
//   REACT_APP_COGNITO_DOMAIN     <- CognitoHostedUiDomain
//   REACT_APP_COGNITO_CLIENT_ID  <- CognitoUserPoolClientId
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || '';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '';
const IDP_NAME = process.env.REACT_APP_COGNITO_IDP || 'AmazonFederate';

// Detect environment for redirect URI
const REDIRECT_URI = window.location.origin + '/callback';

export function getLoginUrl(): string {
  return `https://${COGNITO_DOMAIN}/oauth2/authorize?response_type=code&client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&identity_provider=${IDP_NAME}&scope=openid+email+profile`;
}

export function getLogoutUrl(): string {
  return `https://${COGNITO_DOMAIN}/logout?client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location.origin + '/login')}`;
}

export function getTokenFromStorage(): string | null {
  return sessionStorage.getItem('loft_id_token');
}

export function getEmailFromStorage(): string | null {
  return sessionStorage.getItem('loft_email');
}

export function setTokens(idToken: string, email: string): void { // hawkeye-suppress: rev-006 — stores the runtime JWT in sessionStorage, not a hardcoded secret
  sessionStorage.setItem('loft_id_token', idToken);
  sessionStorage.setItem('loft_email', email);
}

export function clearTokens(): void {
  sessionStorage.removeItem('loft_id_token');
  sessionStorage.removeItem('loft_email');
}

export function isCallbackUrl(): boolean {
  return window.location.pathname === '/callback' && window.location.search.includes('code=');
}

export function getCallbackCode(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('code');
}

export async function exchangeCodeForTokens(code: string): Promise<{ id_token: string; email: string }> { // hawkeye-suppress: rev-006 — OAuth code-for-token exchange; no hardcoded secret (public client_id, runtime tokens)
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    code,
  });

  try {
    const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    const data = await res.json();

    if (data.error) {
      throw new Error(`Token error: ${data.error} - ${data.error_description || ''}`);
    }

    // Decode JWT payload to get email
    const payload = decodeToken(data.id_token);
    const email = payload?.email || payload?.sub || '';

    return { id_token: data.id_token, email }; // hawkeye-suppress: rev-006 — runtime id_token from the token endpoint, not a secret
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('Token error:')) throw err;
    throw new Error(`Failed to exchange auth code: ${String(err)}`);
  }
}

/**
 * Decode a JWT token payload without verifying the signature.
 * Note: Signature verification happens server-side via the API Gateway
 * Cognito authorizer. This is used client-side only for extracting
 * display info and checking expiry.
 */
export function decodeToken(token: string): TokenPayload | null { // hawkeye-suppress: rev-006 — decodes a runtime JWT argument, not a secret
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as TokenPayload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean { // hawkeye-suppress: rev-006 — reads a runtime JWT's exp claim, not a secret
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  return Date.now() >= payload.exp * 1000;
}
